<script>
  import t from '#lib/i18n.svelte.js';
  import SearchSelect from '#lib/SearchSelect.svelte';
  import FormLabel from '#lib/FormLabel.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import { normalize_lock_identifiers } from '#lib/loxone_lock_utils.js';

  let { entry, is_editing } = $props();

  const EMPTY_LOCK_OPTION_VALUE = '';
  const MEETING_ROOM_RESOURCE_TYPE = 'meeting_room';

  let loxone_lock_catalog_entries = $state([]);
  let loxone_lock_catalog_meta = $state({
    source: undefined,
    synced_at: undefined,
    sync_error: undefined,
    is_stale: false,
  });
  let is_loxone_lock_catalog_loading = $state(false);
  let loxone_lock_catalog_error = $state('');
  let lock_catalog_loaded_entry_id = $state();
  let lock_catalog_request_sequence = 0;

  let loxone_lock_entries_by_id = $derived.by(() => {
    return new Map(
      loxone_lock_catalog_entries.map((lock_entry) => [lock_entry.lock_id, lock_entry])
    );
  });

  function get_entry_key_for_catalog_sync() {
    return entry?.entry_id ? String(entry.entry_id) : 'new';
  }

  function ensure_lock_selector_rows() {
    if (!Array.isArray(entry.lock_ids)) {
      entry.lock_ids = [''];
      return;
    }
    if (entry.lock_ids.length === 0) {
      entry.lock_ids = [''];
    }
  }

  function get_primary_selected_lock_id() {
    const normalized_lock_ids = normalize_lock_identifiers(entry.lock_ids);
    return normalized_lock_ids[0];
  }

  function get_primary_selected_room_id() {
    const primary_lock_id = get_primary_selected_lock_id();
    if (!primary_lock_id) return undefined;
    return loxone_lock_entries_by_id.get(primary_lock_id)?.room_id;
  }

  function get_lock_assignment_description(lock_entry) {
    if (!lock_entry?.is_assigned_elsewhere) return undefined;

    const assigned_resource_titles = (lock_entry.assigned_resource_titles || []).filter(Boolean);
    if (assigned_resource_titles.length === 0) {
      return t('loxone_lock_assigned_elsewhere_short');
    }

    return t('loxone_lock_assigned_elsewhere_to_short', {
      resources: assigned_resource_titles.join(', '),
    });
  }

  function get_lock_option_description(lock_entry) {
    const description_parts = [`${t('lock_id')}: ${lock_entry.lock_id}`];

    if (lock_entry?.access_output_label) {
      description_parts.push(lock_entry.access_output_label);
    }

    const assignment_description = get_lock_assignment_description(lock_entry);
    if (assignment_description) {
      description_parts.push(assignment_description);
    }

    return description_parts.join(' • ');
  }

  function get_lock_select_options(index) {
    const current_lock_id =
      typeof entry.lock_ids?.[index] === 'string' ? entry.lock_ids[index] : '';
    const selected_lock_id_set = new Set(normalize_lock_identifiers(entry.lock_ids));

    const lock_options = [
      {
        value: EMPTY_LOCK_OPTION_VALUE,
        title: t('no_lock_selected'),
        description: t('no_lock_selected_hint'),
      },
    ];

    for (const lock_entry of loxone_lock_catalog_entries) {
      const is_selected_in_other_slot =
        selected_lock_id_set.has(lock_entry.lock_id) &&
        lock_entry.lock_id !== current_lock_id;
      const room_label = lock_entry.room_name || lock_entry.room_id || t('unknown_room');
      const option_description = [room_label, get_lock_option_description(lock_entry)]
        .filter(Boolean)
        .join(' • ');

      lock_options.push({
        value: lock_entry.lock_id,
        title: lock_entry.lock_name,
        description: option_description,
        disabled: Boolean(is_selected_in_other_slot),
      });
    }

    if (current_lock_id && !loxone_lock_entries_by_id.has(current_lock_id)) {
      lock_options.push({
        value: current_lock_id,
        title: current_lock_id,
        description: [t('unknown_lock'), t('loxone_lock_unknown_warning')].join(' • '),
      });
    }

    return lock_options;
  }

  function build_loxone_lock_catalog_endpoint(force_refresh = false) {
    const search_params = new URLSearchParams();
    if (force_refresh) search_params.set('refresh', '1');
    if (entry.entry_id) search_params.set('current_entry_id', String(entry.entry_id));

    const query_string = search_params.toString();
    if (!query_string) return '/api/loxone-lock-catalog';

    return `/api/loxone-lock-catalog?${query_string}`;
  }

  function get_room_id_for_display() {
    const selected_room_id = get_primary_selected_room_id();
    if (selected_room_id) return selected_room_id;
    return entry.room_id || '';
  }

  function get_room_name_for_display() {
    const room_id = get_room_id_for_display();
    if (!room_id) return t('unknown_room');

    const lock_entry_for_room = loxone_lock_catalog_entries.find(
      (lock_entry) => lock_entry.room_id === room_id
    );

    if (!lock_entry_for_room?.room_name) return room_id;
    return lock_entry_for_room.room_name;
  }

  function add_lock_selector_row() {
    if (!Array.isArray(entry.lock_ids)) {
      entry.lock_ids = [];
    }
    entry.lock_ids.push('');
  }

  function remove_lock_selector_row(index) {
    if (!Array.isArray(entry.lock_ids)) return;

    entry.lock_ids.splice(index, 1);
    if (entry.lock_ids.length === 0) {
      entry.lock_ids = [''];
    }
  }

  async function load_loxone_lock_catalog({ force_refresh = false } = {}) {
    if (!is_editing || entry.resource_type !== MEETING_ROOM_RESOURCE_TYPE) return;

    const current_entry_id = get_entry_key_for_catalog_sync();
    const request_sequence = ++lock_catalog_request_sequence;
    is_loxone_lock_catalog_loading = true;
    loxone_lock_catalog_error = '';

    try {
      const endpoint = build_loxone_lock_catalog_endpoint(force_refresh);
      const response = await fetch(endpoint, { method: 'GET' });
      if (!response.ok) {
        const error_text = await response.text();
        throw new Error(error_text || response.statusText);
      }
      const response_json = await response.json();

      if (request_sequence !== lock_catalog_request_sequence) return;
      if (!is_editing || entry.resource_type !== MEETING_ROOM_RESOURCE_TYPE) return;
      if (current_entry_id !== get_entry_key_for_catalog_sync()) return;

      loxone_lock_catalog_entries = response_json?.locks || [];
      loxone_lock_catalog_meta = {
        source: response_json?.source,
        synced_at: response_json?.synced_at,
        sync_error: response_json?.sync_error,
        is_stale: Boolean(response_json?.is_stale),
      };
      lock_catalog_loaded_entry_id = current_entry_id;

      const normalized_lock_ids = normalize_lock_identifiers(entry.lock_ids);
      entry.lock_ids = normalized_lock_ids.length > 0 ? normalized_lock_ids : [''];
    } catch (error) {
      if (request_sequence !== lock_catalog_request_sequence) return;
      loxone_lock_catalog_error = error?.message || t('loxone_lock_catalog_load_failed');
      console.error('Failed to load Loxone lock catalog:', error);
    } finally {
      if (request_sequence === lock_catalog_request_sequence) {
        is_loxone_lock_catalog_loading = false;
      }
    }
  }

  $effect(() => {
    if (!is_editing || entry.resource_type !== MEETING_ROOM_RESOURCE_TYPE) return;

    ensure_lock_selector_rows();
    const current_entry_key = get_entry_key_for_catalog_sync();
    if (lock_catalog_loaded_entry_id === current_entry_key) return;

    load_loxone_lock_catalog({ force_refresh: true });
  });

  $effect(() => {
    if (!is_editing || entry.resource_type !== MEETING_ROOM_RESOURCE_TYPE) return;

    const primary_selected_lock_id = get_primary_selected_lock_id();
    if (!primary_selected_lock_id) {
      return;
    }

    const selected_lock_entry = loxone_lock_entries_by_id.get(primary_selected_lock_id);
    if (!selected_lock_entry?.room_id) return;

    if (entry.room_id !== selected_lock_entry.room_id) {
      entry.room_id = selected_lock_entry.room_id;
    }
  });

  $effect(() => {
    if (is_editing && entry.resource_type === MEETING_ROOM_RESOURCE_TYPE) return;
    lock_catalog_loaded_entry_id = undefined;
    loxone_lock_catalog_error = '';
  });
</script>

{#if entry.resource_type === MEETING_ROOM_RESOURCE_TYPE && is_editing}
  <div class='form-row gap-x g-5 items-start'>
    <FormLabel class='no-padding'>{t('room_id')}</FormLabel>
    <div class='flex-column gap-y g-2 items-start'>
      <p class='text-body'>{get_room_name_for_display()}</p>
      <p class='text-caption text-3 identifier-text'>
        {get_room_id_for_display() || t('room_id_not_set')}
      </p>
    </div>
  </div>

  <div class='form-row gap-x g-5 items-start'>
    <FormLabel class='no-padding' for='lock_id_1'>{t('locks')}</FormLabel>
    <div class='flex-column gap-y g-4 items-start lock-editor-column'>
      <div class='flex-row gap-x g-2 items-center'>
        <button
          class='button-label'
          onclick={() => load_loxone_lock_catalog({ force_refresh: true })}
          disabled={is_loxone_lock_catalog_loading}
        >
          <ButtonLabel start_icon='rotate-left' variant='brand ghostly'>
            {is_loxone_lock_catalog_loading ? t('syncing_loxone_locks') : t('sync_loxone_locks')}
          </ButtonLabel>
        </button>

        {#if loxone_lock_catalog_meta.source === 'fallback_file'}
          <p class='text-caption text-3'>{t('loxone_lock_sync_fallback_active')}</p>
        {:else if loxone_lock_catalog_meta.sync_error}
          <p class='text-caption text-3'>{t('loxone_lock_sync_cached_data')}</p>
        {:else if loxone_lock_catalog_meta.synced_at}
          <p class='text-caption text-3'>{t('loxone_lock_sync_ready')}</p>
        {/if}
      </div>

      {#if loxone_lock_catalog_error}
        <p class='text-caption text-error'>{loxone_lock_catalog_error}</p>
      {/if}

      {#each entry.lock_ids as _lock_identifier, index}
        <div class='flex-column gap-y g-2 items-start lock-row'>
          <div class='flex-row gap-x g-2 items-center lock-row-controls'>
            <SearchSelect
              search_input_id={`lock_id_${index + 1}`}
              options={get_lock_select_options(index)}
              bind:value={entry.lock_ids[index]}
              placeholder={t('select_lock')}
              variant='brand outlined'
              width='full'
              allow_null={false}
              min_search_term_length={1}
              disabled={is_loxone_lock_catalog_loading}
            />
            <button
              class='button-label'
              onclick={() => remove_lock_selector_row(index)}
              disabled={entry.lock_ids.length <= 1}
            >
              <ButtonLabel start_icon='trash' variant='brand ghostly' />
            </button>
          </div>
          <p class='text-caption text-3 identifier-text'>
            {entry.lock_ids[index] || t('lock_id_not_set')}
          </p>
        </div>
      {/each}

      <button class='button-label' onclick={add_lock_selector_row}>
        <ButtonLabel start_icon='plus' variant='brand ghostly'>
          {t('add_lock')}
        </ButtonLabel>
      </button>

      <p class='text-caption text-3'>
        {t('loxone_lock_reassignment_notice')}
      </p>
    </div>
  </div>
{/if}

<style lang='postcss'>
  .lock-editor-column {
    width: 100%;
  }

  .lock-row {
    width: 100%;
  }

  .lock-row-controls {
    width: 100%;
  }

  .identifier-text {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
      "Courier New", monospace;
    overflow-wrap: anywhere;
    text-transform: none;
    letter-spacing: 0;
  }
</style>
